import { createElement, CSSProperties, FC } from 'react';
import { Avatar, Badge } from 'antd';
import type { AvatarProps } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';
import { resolveCorporateLogo, themedCorporateLogoUrl } from '@/utilities';

import {
    getWorkspaceFolderIconType,
    getWorkspaceResourceIconType,
    normalizeWorkspaceListType,
    normalizeWorkspaceResourceKind,
    WORKSPACE_LIST_TYPE_ICON_REGISTRY,
} from './configuration';
import type { WorkspaceFolderGlyphVariant } from './configuration';

export interface WorkspaceFolderGlyphProps {
    color?: string;
    fill?: string;
    size?: number | string;
    style?: CSSProperties;
    variant?: WorkspaceFolderGlyphVariant;
}

export const WorkspaceFolderGlyph: FC<WorkspaceFolderGlyphProps> = ({
    color,
    fill,
    size = '1em',
    style,
    variant = 'default',
}) => {
    const { token } = useAntDesignTheme();
    const Icon = getWorkspaceFolderIconType(variant);

    return createElement(Icon, {
        color: color ?? token.gold6,
        fill: fill ?? token.gold2,
        size,
        style,
    });
};

export interface WorkspaceResourceGlyphProps {
    active?: boolean;
    color?: string;
    fill?: string;
    resource: string;
    size?: number | string;
    style?: CSSProperties;
}

export const WorkspaceResourceGlyph: FC<WorkspaceResourceGlyphProps> = ({
    active = false,
    color,
    fill,
    resource,
    // Match react-icons' native sizing contract. `1em` follows the typography
    // of compact navigation and collapsible buttons instead of escaping to the
    // root font size. Avatar contexts opt into their own percentage below.
    size = '1em',
    style,
}) => {
    const kind = normalizeWorkspaceResourceKind(resource);
    const isFolder = kind === 'folder';
    const Icon = getWorkspaceResourceIconType(kind);
    const iconStyle = {
        ...style,
        ...(kind === 'investment' ? { transform: 'rotate(-90deg)' } : undefined),
    };

    if (!isFolder) {
        return createElement(Icon, { color, size, style: iconStyle });
    }

    return (
        <WorkspaceFolderGlyph
            color={color}
            fill={fill}
            size={size}
            style={iconStyle}
            variant={active ? 'open' : 'default'}
        />
    );
};

export interface WorkspaceResourceAvatarProps extends Omit<AvatarProps, 'icon' | 'shape' | 'size' | 'src'> {
    active?: boolean;
    listType?: string;
    name?: string;
    resource: string;
    size?: number;
    src?: AvatarProps['src'];
    ticker?: string;
    website?: string;
}

const WorkspaceResourceAvatar: FC<WorkspaceResourceAvatarProps> = ({
    active = false,
    listType,
    name,
    resource,
    size = 20,
    src,
    style,
    ticker,
    website,
    ...avatarProps
}) => {
    const { resolvedThemeName, token } = useAntDesignTheme();
    const kind = normalizeWorkspaceResourceKind(resource);
    const isFolder = kind === 'folder';
    const normalizedListType = kind === 'list' && listType ? normalizeWorkspaceListType(listType) : undefined;
    const logoTheme = String(resolvedThemeName).includes('dark') ? 'dark' : 'light';
    const resolvedSrc =
        kind === 'investment'
            ? resolveCorporateLogo(
                  website,
                  typeof src === 'string' ? src : undefined,
                  { theme: logoTheme },
                  { name, ticker },
              )
            : typeof src === 'string'
              ? themedCorporateLogoUrl(src, logoTheme)
              : src;

    const avatar = (
        <Avatar
            {...avatarProps}
            size={size}
            shape='square'
            src={resolvedSrc}
            icon={
                isFolder ? (
                    <WorkspaceResourceGlyph
                        active={active}
                        color={token.gold6}
                        fill={token.gold2}
                        resource={kind}
                        size='75%'
                    />
                ) : (
                    <WorkspaceResourceGlyph
                        active={active}
                        color={token.colorTextTertiary}
                        resource={kind}
                        size='75%'
                    />
                )
            }
            style={{
                border: `1px solid ${token.colorBorder}`,
                backgroundColor: token.colorBorderBg,
                flex: '0 0 auto',
                ...style,
            }}
        />
    );

    if (!normalizedListType) {
        return avatar;
    }

    const ListTypeIcon = WORKSPACE_LIST_TYPE_ICON_REGISTRY[normalizedListType];
    const listTypeConfiguration = {
        companies: {
            backgroundColor: token.gold1,
            borderColor: token.gold2,
            color: token.gold5,
            label: 'Companies List',
        },
        custom: {
            backgroundColor: token.purple1,
            borderColor: token.purple2,
            color: token.purple5,
            label: 'Custom List',
        },
        people: {
            backgroundColor: token.blue1,
            borderColor: token.blue2,
            color: token.blue5,
            label: 'People List',
        },
    }[normalizedListType];
    // Keep compact navigation unchanged while making larger list-row subtype
    // markers slightly smaller and seating them farther into the bottom-right
    // corner of the primary avatar.
    const badgeSize = Math.min(22, Math.max(12, Math.round(size * 0.55)));

    return (
        <Badge
            count={
                // Badge positions custom indicators by cloning its immediate DOM
                // child with the `ant-scroll-number-custom-component` class. A
                // tooltip component at this boundary swallows that class, which
                // leaves the list-type avatar in normal document flow. Keep this
                // element as the positioning boundary and render the tooltip
                // inside it so every list surface receives the same overlay.
                <span data-testid='workspace-resource-list-type-badge'>
                    <SmallTooltip title={listTypeConfiguration.label} placement='right' padding='0 0.25rem'>
                        <Avatar
                            aria-label={listTypeConfiguration.label}
                            icon={createElement(ListTypeIcon, { color: listTypeConfiguration.color, size: '75%' })}
                            shape='square'
                            size={badgeSize}
                            style={{
                                backgroundColor: listTypeConfiguration.backgroundColor,
                                border: `1px solid ${listTypeConfiguration.borderColor}`,
                                borderRadius: token.borderRadius,
                            }}
                        />
                    </SmallTooltip>
                </span>
            }
            offset={[-Math.round(badgeSize / 2), size - Math.round(badgeSize / 3)]}
        >
            {avatar}
        </Badge>
    );
};

export default WorkspaceResourceAvatar;
